/**
 * Tier Bands — Single source of truth for tier-band thresholds and DOK expectations.
 *
 * AUDIT v1.4 / Sprint 4 (V2) — Consolidates band-resolution and DOK-by-band
 * tables that were previously duplicated in:
 *   - pipeline/stages/pedagogyStage.ts  (MIN_DOK_BY_TIER_BAND, MAX_DOK_BY_TIER_BAND)
 *   - practice/logic/pedagogicalGuard.ts (MIN_DOK_FOR_BAND, local getTierBand)
 *
 * The values matched today but were independently maintained — drift between
 * pipeline-side validation and runtime-guard would have caused stille
 * divergentie tussen "wat de governance zegt" en "wat de leerling ervaart".
 *
 * After this consolidation: any change to band thresholds or DOK floors/ceilings
 * happens here once, and both consumers automatically agree.
 *
 * NOTE: `getTierBand()` is intentionally re-exported from `pipeline/policyEngine`
 * to avoid breaking existing imports while making this file the canonical place
 * for tier-band reasoning.
 */

import { getTierBand } from './pipeline/policyEngine'
import type { CognitiveLevel } from './cognitiveLevel'

/** Re-export so consumers have a single import for all tier-band concerns. */
export { getTierBand }

/** Tier-band string literals — keep aligned with policyEngine.getTierBand(). */
export type TierBand = 'basic' | 'intermediate' | 'advanced' | 'expert'

/**
 * Minimum DOK level expected per tier band.
 * - basic (tiers 1-4):        DOK 1 — recall is fine
 * - intermediate (tiers 5-8): DOK 1 — recall still acceptable
 * - advanced (tiers 9-12):    DOK 2 — at least application
 * - expert (tiers 13-16):     DOK 3 — strategic thinking required
 *
 * IMPORTANT (post P4): this raw map is NO LONGER the floor *decision* source.
 * Since the P4 calibration the floor decision flows through the module-aware
 * `getMinDokForBand(band, module)` (see below). This map is the
 * procedural-math default that `getMinDokForBand` falls back to when no
 * module profile applies — and the SSOT that `selectionConfig.MIN_DOK_FOR_TIER_BAND`
 * reference-aliases (GSR-22). Treat direct reads of this map as "raw default"
 * and prefer `getMinDokForBand()` wherever module context exists.
 *
 * Read-sites (clarified — GOVERNANCE_DETANGLE_PLAN_2026 §16.3):
 *   - checks/pedagogicalChecks.ts → checkDokFloor()  → DECISION via getMinDokForBand()
 *   - pipeline/stages/pedagogyStage.ts → checkDokAlignment() delegates the
 *     decision to checkDokFloor(); it reads this raw map only as a message
 *     fallback (see §16.5).
 *   - practice/logic/pedagogicalGuard.ts → evaluateQuestion()
 *
 * P4 CALIBRATION (mei 2026, GOVERNANCE_TAA_TRIAGE):
 *   Use `getMinDokForBand(band, module)` instead of reading this map
 *   directly when module context is available — TAA reading-comprehension
 *   has legitimate DOK-2 begrip-tasks (CONCLUSIE/STRUCTUUR/VERB) at the
 *   expert tier that should not be flagged as DOK_TOO_LOW.
 */
export const MIN_DOK_BY_TIER_BAND: Readonly<Record<TierBand, number>> =
  Object.freeze({
    basic: 1,
    intermediate: 1,
    advanced: 2,
    expert: 3,
  })

/**
 * Module cognitive profile — classifies a module by the dominant cognitive
 * demand of its question bank. Drives module-aware DOK floors via
 * `DOK_FLOOR_BY_PROFILE`.
 *
 * Why this replaces the previous ad-hoc `MODULE_DOK_FLOOR_OVERRIDES` map
 * (which only covered Taalbegrip):
 *
 * Webb's DOK is calibrated to math/science problem-solving. Forcing the
 * same DOK 3 floor on the expert tier (13-16) for every Dutch primary
 * module produces false positives whenever the module's authentic
 * cognitive demand is recall (Topo capitals, loanword spelling) or
 * comprehension (text structure, study skills) — those are inherently
 * DOK 1-2 even at group-8 level. The profile encodes the legitimate
 * floor per module class, so seeds stay correct AND the pipeline
 * stops flagging them.
 *
 *  - 'procedural-math'      — full Webb-style escalation (REK, GET, MET,
 *                             TIJD, GELD). Strategic thinking expected on
 *                             expert tier.
 *  - 'application-language' — language application (SWW, TAA). Expert tier
 *                             tops out at DOK 2 (begrip/toepassen).
 *  - 'recall-knowledge'     — fact recall (TOPO, SPR — loanwords/spelling
 *                             rules). DOK 1 across all bands.
 *  - 'comprehension'        — strategy/study skills (STU). Expert tier
 *                             tops out at DOK 2 (begrip).
 *
 * GOVERNANCE_REVIEW_TRIAGE_2026 + STRATEGIC_VALIDATION_LDG_TIER_CONTENT_2026.
 */
export type ModuleCognitiveProfile =
  | 'procedural-math'
  | 'application-language'
  | 'recall-knowledge'
  | 'comprehension'

/**
 * Per-module cognitive profile assignment.
 *
 * Keyed by `ModuleId` string so this file stays decoupled from the
 * `content/types.ts` enum (governance does not import from content
 * domain). The 10 entries below MUST stay aligned with `ModuleId` in
 * `src/content/types.ts`; the unit-test suite asserts exhaustive
 * coverage.
 */
const MODULE_COGNITIVE_PROFILES: Readonly<
  Record<string, ModuleCognitiveProfile>
> = Object.freeze({
  // Procedural math — full DOK escalation
  Basisrekenen: 'procedural-math',
  Getalbegrip: 'procedural-math',
  Metriek: 'procedural-math',
  Tijd: 'procedural-math',
  Geld: 'procedural-math',
  // Language application — expert caps at DOK 2
  Taalbegrip: 'application-language',
  SpellingWW: 'application-language',
  // Recall knowledge — DOK 1 across all bands
  Topo: 'recall-knowledge',
  SpellingRegel: 'recall-knowledge',
  // Comprehension / study strategies — expert caps at DOK 2
  Studievaardig: 'comprehension',
})

/**
 * DOK floor per (profile, tier-band) pair.
 *
 * The 'procedural-math' row is identical to `MIN_DOK_BY_TIER_BAND` —
 * that is by design: the default reflects the math-procedural pedagogy
 * that Webb originally calibrated DOK for.
 */
const DOK_FLOOR_BY_PROFILE: Readonly<
  Record<ModuleCognitiveProfile, Readonly<Record<TierBand, number>>>
> = Object.freeze({
  'procedural-math': Object.freeze({
    basic: 1,
    intermediate: 1,
    advanced: 2,
    expert: 3,
  }),
  'application-language': Object.freeze({
    basic: 1,
    intermediate: 1,
    advanced: 2,
    expert: 2, // begrip/toepassen — niet strategisch denken
  }),
  'recall-knowledge': Object.freeze({
    basic: 1,
    intermediate: 1,
    advanced: 1,
    expert: 1, // recall blijft recall, ook op E8+
  }),
  comprehension: Object.freeze({
    basic: 1,
    intermediate: 1,
    advanced: 2,
    expert: 2, // begrip is het plafond voor study-skill families
  }),
})

/**
 * Resolve the minimum DOK floor for a tier-band, with module-aware
 * cognitive-profile lookup.
 *
 * Lookup order:
 *  1. If `module` is provided AND known in MODULE_COGNITIVE_PROFILES,
 *     use the floor from that module's profile.
 *  2. Otherwise fall back to MIN_DOK_BY_TIER_BAND (procedural-math
 *     defaults), which preserves behaviour for any future module not
 *     yet classified.
 *
 * Signature unchanged from the previous version — all callers
 * (pedagogicalChecks.checkDokFloor, future runtime consumers) keep
 * working without modification.
 */
export function getMinDokForBand(
  band: TierBand,
  module?: string,
  skill?: string,
): number {
  if (module) {
    const profile = MODULE_COGNITIVE_PROFILES[module]
    if (profile) {
      // Sub-domain (skill) specific overrides for procedural-math
      if (profile === 'procedural-math' && band === 'expert' && skill) {
        if (skill === 'machten_wortels' || skill === 'negatieve_getallen') {
          return 2 // DOK 2 is the authentic cognitive demand for these skills
        }
      }
      return DOK_FLOOR_BY_PROFILE[profile][band]
    }
  }
  return MIN_DOK_BY_TIER_BAND[band] ?? 1
}

/**
 * Read-only accessor for the per-module cognitive-profile assignment.
 *
 * Additive, export-only (no behaviour change). Exposes the otherwise
 * module-private `MODULE_COGNITIVE_PROFILES` map so data-driven consumers
 * (e.g. the SSOT registry surfaced in the admin) can read the LIVE profile
 * mapping instead of duplicating it. Returns the frozen SSOT object directly,
 * so callers always reflect the current code.
 */
export function getModuleCognitiveProfiles(): Readonly<
  Record<string, ModuleCognitiveProfile>
> {
  return MODULE_COGNITIVE_PROFILES
}

/**
 * Read-only accessor for the per-(profile, tier-band) DOK floor matrix.
 *
 * Additive, export-only. Lets the SSOT registry render the full floor matrix
 * live without re-reading the private table. The floor *decision* still flows
 * through `getMinDokForBand()`.
 */
export function getDokFloorByProfile(): Readonly<
  Record<ModuleCognitiveProfile, Readonly<Record<TierBand, number>>>
> {
  return DOK_FLOOR_BY_PROFILE
}

/**
 * Minimum cognitive (Bloom) level the plus-/tier-band gate expects per
 * (profile, tier-band) pair. C1 (GOVERNANCE_SYSTEM_HARDENING_AUDIT_2026-06 §1):
 * this is the profile-aware twin of `DOK_FLOOR_BY_PROFILE` for the cognition
 * gate, replacing the formerly hardcoded `getPlusRequirements()` values that
 * demanded `analysis`/`application` for every module with only an inline Topo
 * carve-out.
 *
 *  - 'procedural-math'      — PRESERVED VERBATIM from the legacy hardcoded plus
 *                             demands (application on basic/inter/advanced,
 *                             analysis on expert). Math behaviour is unchanged.
 *  - 'application-language' — expert relaxed `analysis` → `comprehension`
 *                             (DOK-2 begrip ceiling). Fixes the false
 *                             CONTENT_PLUS_INADEQUATE on TAA-LEZEN-STRUCTUUR.
 *  - 'comprehension'        — same shape; fixes STU-SAMENVATTEN expert-plus.
 *  - 'recall-knowledge'     — `recall` in every band → no escalation demanded.
 *                             Generalizes the inline `module !== 'Topo'`
 *                             carve-out so SpellingRegel is exempt too.
 *
 * One SSOT for "how deep must module X be on band Y" — read via
 * `getCognitiveFloorForBand()`, parallel to `getMinDokForBand()`.
 */
const COGNITIVE_FLOOR_BY_PROFILE: Readonly<
  Record<ModuleCognitiveProfile, Readonly<Record<TierBand, CognitiveLevel>>>
> = Object.freeze({
  'procedural-math': Object.freeze({
    basic: 'application',
    intermediate: 'application',
    advanced: 'application',
    expert: 'analysis',
  }),
  'application-language': Object.freeze({
    basic: 'application',
    intermediate: 'application',
    advanced: 'application',
    expert: 'comprehension', // begrip-plafond — geen strategisch denken eisen
  }),
  comprehension: Object.freeze({
    basic: 'application',
    intermediate: 'application',
    advanced: 'application',
    expert: 'comprehension',
  }),
  'recall-knowledge': Object.freeze({
    basic: 'recall',
    intermediate: 'recall',
    advanced: 'recall',
    expert: 'recall', // recall blijft recall — geen cognitie-escalatie eisen
  }),
})

/**
 * Resolve the minimum cognitive (Bloom) floor for a tier-band, module-aware.
 *
 * Lookup order mirrors `getMinDokForBand`:
 *  1. Known module → its profile's floor from COGNITIVE_FLOOR_BY_PROFILE.
 *  2. Otherwise → the 'procedural-math' row (preserves the legacy hardcoded
 *     demands for any future unclassified module).
 */
export function getCognitiveFloorForBand(
  band: TierBand,
  module?: string,
): CognitiveLevel {
  if (module) {
    const profile = MODULE_COGNITIVE_PROFILES[module]
    if (profile) {
      return COGNITIVE_FLOOR_BY_PROFILE[profile][band]
    }
  }
  return COGNITIVE_FLOOR_BY_PROFILE['procedural-math'][band]
}

/**
 * Read-only accessor for the per-(profile, tier-band) cognitive-floor matrix.
 * Additive, export-only — lets the SSOT registry render the live matrix
 * (parallel to `getDokFloorByProfile`). The floor *decision* flows through
 * `getCognitiveFloorForBand()`.
 */
export function getCognitiveFloorByProfile(): Readonly<
  Record<ModuleCognitiveProfile, Readonly<Record<TierBand, CognitiveLevel>>>
> {
  return COGNITIVE_FLOOR_BY_PROFILE
}

/**
 * Maximum DOK level "naturally expected" per tier band.
 * Higher DOK on lower-band questions is allowed but flagged as informational
 * (PEDAGOGY_DOK_TOO_HIGH) so authors can sanity-check unusually deep questions
 * on early tiers.
 */
export const MAX_DOK_BY_TIER_BAND: Record<TierBand, number> = {
  basic: 2,
  intermediate: 3,
  advanced: 4,
  expert: 4,
}

// NOTE: `getDokExpectations(band)` was removed in RC-3 (G-17, audit 2026)
// as a dead export — zero source-code consumers. Both DOK-by-band tables above
// remain in use by pedagogyStage and pedagogicalGuard directly. If a future
// consumer wants the combined min/max accessor, recreate a 3-line helper.
